/* @flow */
import _ from 'lodash';
import cx from 'classnames';
import * as React from 'react';

import ConfirmDialog from 'components/ConfirmDialog';
import CurrentUser from 'auth/CurrentUser';
import Glyph from 'components/Glyph';
import Menu from 'components/Menu';
import MenuItem from 'components/MenuItem';
import Text from 'components/Text';
import Image from 'components/Image';
import MediaObject from 'components/MediaObject';
import { mountModal } from 'lib/modal';
import { patchRoles, patchCurrentUserRoles } from 'admin/actions/OrgRoleActions';
import { rolesFlags, roleListFromFlags } from 'utils/roleHelpers';
import errorLogger from 'utils/errorLogger';

import type { PotentialRole } from 'groups/components/OrgRoleDropdown';
import type { RoleFlags } from 'utils/roleHelpers';
import type { OrganizationRole } from 'queries/__generated__/globalGQLTypes';

import './styles/OrgRoleDropdownMenu.scss';

import AppleImage from 'assets/images/apple.svg';
import HeartImage from 'assets/images/heart.svg';
import PencilImage from 'assets/images/pencil.svg';
import BellImage from 'assets/images/bell.svg';

type Props = {
  organizationId: number,
  roles: OrganizationRole[],
  potentialRoles: PotentialRole[],
  userId: Backbone$Id,
  isCurrentUser?: boolean,
  onClose?: (roles: OrganizationRole[]) => void,
  onFinishSave?: () => void,
  onRequestHide?: () => void,
};

class OrgRoleDropdownMenu extends React.Component<Props> {
  constructor(props: Props) {
    super(props);
    this.checkedRoles = rolesFlags(props.roles);
  }

  componentWillReceiveProps(nextProps: Props): void {
    if (!_.isEqual(this.props.roles, nextProps.roles)) {
      this.checkedRoles = rolesFlags(nextProps.roles);
    }
  }

  checkedRoles: RoleFlags;

  handleClose = () => {
    const { organizationId, userId, roles, onClose } = this.props;
    const checkedRolesList = roleListFromFlags(this.checkedRoles);
    if (_.xor(roles, checkedRolesList).length) {
      const commitPatchRole = () => {
        const patchFn = this.props.isCurrentUser ? patchCurrentUserRoles : patchRoles;

        // $FlowFixMe
        patchFn(organizationId, userId, checkedRolesList)
          .then(() => {
            const { onFinishSave } = this.props;
            if (onFinishSave) {
              onFinishSave();
            }
          })
          .catch(errorLogger('OrgRoleDropdownMenu handleClose patch failed'));

        if (onClose) {
          onClose(checkedRolesList);
        }
      };

      const revertPatchRole = () => {
        this.checkedRoles = rolesFlags(this.props.roles);
      };

      if (CurrentUser.getUserId() === userId) {
        try {
          mountModal(
            <ConfirmDialog
              eventScreenName="OrgRoleDropdownMenu"
              title={'Are you sure you want to change your role?'}
              actionButtonText="Yes"
              cancelButtonText="No"
              onConfirm={commitPatchRole}
              onCancel={revertPatchRole}
            />,
            this
          );
        } catch (e) {
          console.warn(
            'known issue: https://app.asana.com/0/52679726735761/403279220843683. the UX is not affected but something bad is happening.'
          );
          console.error('[debug] mountModal in OrgRoleDropdownMenu failed');
        }
      } else {
        commitPatchRole();
      }
    }
    if (this.props.onRequestHide) {
      this.props.onRequestHide();
    }
  };
  renderMenuItem(role: string, roleName: string, icon: string) {
    const checked = this.checkedRoles[role];
    const disabled = role === 'admin' && this.checkedRoles.admin;

    const handleClick = () => {
      const nextRoles = { ...this.checkedRoles };
      nextRoles[role] = !this.checkedRoles[role];
      if (role === 'student' && nextRoles.student) {
        nextRoles.parent = false;
        nextRoles.teacher = false;
      } else if (nextRoles.teacher || nextRoles.parent || nextRoles.admin) {
        nextRoles.student = false;
      }

      if (role === 'admin' && !this.checkedRoles.parent) {
        nextRoles.parent = true;
      }

      this.checkedRoles = nextRoles;
    };

    return (
      <MenuItem
        key={role}
        className={cx('OrgRoleDropdownMenu-item', { 'OrgRoleDropdownMenu-item-hover': !disabled })}
        onClick={disabled ? () => {} : handleClick}>
        <div className="OrgRoleDropdownMenu-item-inner">
          <MediaObject gutter="small" center>
            <Image className="OrgRoleDropdownMenu-item-image" src={icon} />
            <Text className="OrgRoleDropdownMenu-item-label" color={checked ? 'pitch' : 'inherit'}>
              {roleName}
            </Text>
          </MediaObject>
          {checked && <Glyph name="check" size="medium" color="brand" />}
          {disabled && <div className="OrgRoleDropdownMenu-item-disabled" />}
        </div>
      </MenuItem>
    );
  }

  render() {
    const roleToIcon = {
      student: PencilImage,
      parent: HeartImage,
      teacher: AppleImage,
      admin: BellImage,
      rce: BellImage,
    };

    return (
      <Menu {...this.props} className="OrgRoleDropdownMenu" onRequestHide={this.handleClose}>
        {this.props.potentialRoles.map(({ role, display_role }) => {
          return this.renderMenuItem(role, display_role, roleToIcon[role]);
        })}
      </Menu>
    );
  }
}

export default OrgRoleDropdownMenu;
